import React, { useEffect, useState, useCallback } from 'react';
import {
  Table,
  Card,
  Row,
  Col,
  Select,
  Input,
  Tag,
  Button,
  Space,
  Typography,
  Tabs,
  Badge,
  Progress,
  Tooltip,
  message,
  Spin,
} from 'antd';
import {
  ArrowLeftOutlined,
  SearchOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import { ChecklistItem, Checklist, GateSummary, Project, ProjectTeamAssignment } from '../types';
import { useLookups } from '../context/LookupsContext';
import ItemDetailDrawer from '../components/common/ItemDetailDrawer';
import TeamTooltip from '../components/common/TeamTooltip';

const { Title, Text } = Typography;

const ChecklistView: React.FC = () => {
  const { projectId, checklistId } = useParams<{ projectId: string; checklistId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { gates, statuses, priorities, getGateName, getStatusName } = useLookups();

  const [project, setProject] = useState<Project | null>(null);
  const [checklist, setChecklist] = useState<Checklist | null>(null);
  const [items, setItems] = useState<ChecklistItem[]>([]);
  const [gateSummary, setGateSummary] = useState<GateSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState<ChecklistItem | null>(null);
  const [drawerVisible, setDrawerVisible] = useState(false);

  // Filters
  const [selectedGate, setSelectedGate] = useState<number | undefined>(
    searchParams.get('gate') ? parseInt(searchParams.get('gate')!) : undefined
  );
  const [selectedStatus, setSelectedStatus] = useState<string | undefined>();
  const [selectedPriority, setSelectedPriority] = useState<string | undefined>();
  const [searchText, setSearchText] = useState('');

  const loadData = useCallback(async () => {
    if (!checklistId || !projectId) return;
    setLoading(true);
    try {
      const [projectData, checklistData, itemsData, summaryData] = await Promise.all([
        api.getProject(projectId),
        api.getChecklist(checklistId),
        api.getChecklistItems(checklistId, {
          gate: selectedGate,
          status: selectedStatus,
          priority: selectedPriority,
        }),
        api.getGateSummary(checklistId),
      ]);
      setProject(projectData);
      setChecklist(checklistData);
      setItems(itemsData);
      setGateSummary(summaryData);
    } catch (error) {
      message.error('Failed to load checklist');
      console.error('Failed to load checklist:', error);
    } finally {
      setLoading(false);
    }
  }, [checklistId, projectId, selectedGate, selectedStatus, selectedPriority]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Update URL when gate changes
  useEffect(() => {
    if (selectedGate !== undefined) {
      setSearchParams({ gate: selectedGate.toString() });
    } else {
      setSearchParams({});
    }
  }, [selectedGate, setSearchParams]);

  const handleItemClick = (item: ChecklistItem) => {
    setSelectedItem(item);
    setDrawerVisible(true);
  };

  const handleItemUpdate = async () => {
    await loadData();
    // Refresh selected item if still viewing
    if (selectedItem) {
      try {
        const updated = await api.getChecklistItem(selectedItem.id);
        setSelectedItem(updated);
      } catch (error) {
        console.error('Failed to refresh item:', error);
      }
    }
  };

  const getStatusTagColor = (status: string): string => {
    switch (status) {
      case 'approved':
        return 'success';
      case 'resolved':
        return 'processing';
      case 'received':
        return 'cyan';
      case 'in_progress':
        return 'warning';
      case 'requested':
        return 'orange';
      case 'not_started':
      default:
        return 'default';
    }
  };

  const getPriorityTagColor = (priority: string): string => {
    switch (priority) {
      case 'high':
        return 'red';
      case 'med':
        return 'orange';
      case 'low':
        return 'green';
      default:
        return 'default';
    }
  };

  const getRYGColor = (ryg: string): string => {
    switch (ryg) {
      case 'green':
        return '#52c41a';
      case 'yellow':
        return '#faad14';
      case 'red':
      default:
        return '#ff4d4f';
    }
  };

  const filteredItems = items.filter((item) => {
    if (searchText) {
      const search = searchText.toLowerCase();
      const itemText = item.templateItem?.itemText?.toLowerCase() || '';
      const itemCode = item.templateItem?.itemCode?.toLowerCase() || '';
      if (!itemText.includes(search) && !itemCode.includes(search)) {
        return false;
      }
    }
    return true;
  });

  const columns = [
    {
      title: 'Code',
      dataIndex: ['templateItem', 'itemCode'],
      key: 'itemCode',
      width: 80,
      render: (code: string) => <Text code>{code}</Text>,
    },
    {
      title: 'Item',
      dataIndex: ['templateItem', 'itemText'],
      key: 'itemText',
      ellipsis: true,
      render: (text: string, record: ChecklistItem) => (
        <Tooltip title={text}>
          <span style={{ cursor: 'pointer', color: '#1890ff' }} onClick={() => handleItemClick(record)}>
            {record.isNa ? <Text delete>{text}</Text> : text}
          </span>
        </Tooltip>
      ),
    },
    {
      title: 'Gate',
      dataIndex: ['templateItem', 'gateNumber'],
      key: 'gateNumber',
      width: 80,
      render: (gate: number) => <Tag>G{gate}</Tag>,
    },
    {
      title: 'Type',
      dataIndex: ['templateItem', 'itemType'],
      key: 'itemType',
      width: 120,
      render: (type: string) => <Text type="secondary">{type}</Text>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: string, record: ChecklistItem) =>
        record.isNa ? (
          <Tag color="default">N/A</Tag>
        ) : (
          <Space>
            {record.rygStatus && (
              <span
                style={{
                  display: 'inline-block',
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: getRYGColor(record.rygStatus),
                }}
              />
            )}
            <Tag color={getStatusTagColor(status)}>{getStatusName(status)}</Tag>
          </Space>
        ),
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      width: 80,
      render: (priority: string) =>
        priority ? <Tag color={getPriorityTagColor(priority)}>{priority.toUpperCase()}</Tag> : '-',
    },
    {
      title: 'RACI',
      key: 'raci',
      width: 150,
      render: (_: any, record: ChecklistItem) => {
        const ti = record.templateItem;
        if (!ti) return '-';
        return (
          <Space size={4} wrap>
            {ti.approverTeams?.map((t) => (
              <TeamTooltip
                key={`a-${t}`}
                teamCode={t}
                teamAssignments={project?.teamAssignments}
                tagColor="cyan"
              />
            ))}
            {ti.receiverTeams?.map((t) => (
              <TeamTooltip
                key={`r-${t}`}
                teamCode={t}
                teamAssignments={project?.teamAssignments}
                tagColor="blue"
              />
            ))}
          </Space>
        );
      },
    },
  ];

  const tabItems = [
    {
      key: 'all',
      label: (
        <span>
          All Items
          <Badge count={items.length} style={{ marginLeft: 8 }} showZero />
        </span>
      ),
    },
    ...gates.map((gate) => {
      const summary = gateSummary.find((g) => g.gateNumber === gate.gateNumber);
      const count = summary?.total || 0;
      const completed = (summary?.completed || 0) + (summary?.naCount || 0);
      return {
        key: gate.gateNumber.toString(),
        label: (
          <span>
            Gate {gate.gateNumber}
            <Badge
              count={`${completed}/${count}`}
              style={{
                marginLeft: 8,
                backgroundColor: completed === count ? '#52c41a' : '#1890ff',
              }}
              showZero
            />
          </span>
        ),
      };
    }),
  ];

  if (loading && !items.length) {
    return (
      <div style={{ textAlign: 'center', padding: 100 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <Button
        type="text"
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate(`/projects/${projectId}`)}
        style={{ marginBottom: 16 }}
      >
        Back to Project
      </Button>

      <Card style={{ marginBottom: 16 }}>
        <Row gutter={16} align="middle">
          <Col flex="auto">
            <Title level={4} style={{ margin: 0 }}>
              {checklist?.name || 'Checklist'}
            </Title>
            <Text type="secondary">{project?.name}</Text>
          </Col>
          <Col>
            <Space>
              {gateSummary.map((gate) => (
                <Tooltip key={gate.gateNumber} title={`${gate.gateName || getGateName(gate.gateNumber)}: ${gate.completionPercentage}%`}>
                  <Progress
                    type="circle"
                    percent={gate.completionPercentage}
                    size={40}
                    format={() => `G${gate.gateNumber}`}
                    strokeColor={
                      gate.completionPercentage === 100
                        ? '#52c41a'
                        : gate.completionPercentage > 0
                        ? '#faad14'
                        : '#ff4d4f'
                    }
                  />
                </Tooltip>
              ))}
            </Space>
          </Col>
        </Row>
      </Card>

      <Card>
        <Tabs
          activeKey={selectedGate?.toString() || 'all'}
          onChange={(key) => setSelectedGate(key === 'all' ? undefined : parseInt(key))}
          items={tabItems}
        />

        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col flex="auto">
            <Input
              placeholder="Search items..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
              style={{ width: 300 }}
            />
          </Col>
          <Col>
            <Space>
              <Select
                placeholder="Status"
                allowClear
                style={{ width: 150 }}
                value={selectedStatus}
                onChange={setSelectedStatus}
                options={statuses.map((s) => ({ label: s.name, value: s.code }))}
              />
              <Select
                placeholder="Priority"
                allowClear
                style={{ width: 120 }}
                value={selectedPriority}
                onChange={setSelectedPriority}
                options={priorities.map((p) => ({ label: p.name, value: p.code }))}
              />
              <Button icon={<ReloadOutlined />} onClick={loadData} loading={loading}>
                Refresh
              </Button>
            </Space>
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={filteredItems}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 20, showSizeChanger: true, showTotal: (total) => `${total} items` }}
          size="middle"
          onRow={(record) => ({
            onClick: () => handleItemClick(record),
            style: { cursor: 'pointer' },
          })}
          rowClassName={(record) => (record.isNa ? 'ant-table-row-disabled' : '')}
        />
      </Card>

      <ItemDetailDrawer
        item={selectedItem}
        visible={drawerVisible}
        onClose={() => {
          setDrawerVisible(false);
          setSelectedItem(null);
        }}
        onUpdate={handleItemUpdate}
        teamAssignments={project?.teamAssignments}
      />
    </div>
  );
};

export default ChecklistView;
